/**
 * ServiceEstimateCard — shows the estimated service fee and the expected
 * turnaround time (TAT) as two clean stat tiles.
 *
 * Shown in the document-upload phase and the provider workspace so the
 * customer always sees what they will pay and how long the service should take.
 *
 * Layout:
 *   layout="compact" (default) — stats stack vertically. Use in narrow columns
 *                                (e.g. the document-upload sidebar).
 *   layout="expand"            — stacks on mobile, sits in a row on desktop.
 *                                Use in the full-width workspace strip.
 *
 * Values never wrap: each stat owns the full row width when stacked and a wide
 * half when in a row, so price ranges like "₹1,800 – ₹2,500" stay on one line.
 * Only the available stats render; a lone stat fills the card.
 */
import { IndianRupee, Clock } from 'lucide-react';
import { PALETTE_FOREST, PALETTE_MINT } from '../tokens';

interface ServiceEstimateCardProps {
  /** Pre-formatted fee, e.g. "₹999" or "₹1,800 – ₹2,500". */
  feeDisplay?: string | null;
  /** Pre-formatted TAT, e.g. "2-6 days". */
  tatDisplay?: string | null;
  /** Stacking behaviour — see file header. */
  layout?: 'compact' | 'expand';
  /** Optional extra classes for outer margins etc. */
  className?: string;
}

type Stat = {
  key:   string;
  icon:  typeof Clock;
  label: string;
  value: string;
};

export function ServiceEstimateCard({
  feeDisplay,
  tatDisplay,
  layout = 'compact',
  className,
}: ServiceEstimateCardProps) {
  const stats: Stat[] = [
    feeDisplay ? { key: 'fee', icon: IndianRupee, label: 'Estimated Fee', value: feeDisplay } : null,
    tatDisplay ? { key: 'tat', icon: Clock,       label: 'Expected Time', value: tatDisplay } : null,
  ].filter(Boolean) as Stat[];

  if (stats.length === 0) return null;

  const expand = layout === 'expand';
  const rowClass = expand ? 'flex flex-col sm:flex-row' : 'flex flex-col';

  return (
    <div
      className={`rounded-2xl border border-border bg-background p-1.5 shadow-sm ${className ?? ''}`}
    >
      <div className={rowClass}>
        {stats.map((stat, i) => {
          const Icon = stat.icon;
          // Divider between stats — horizontal when stacked, vertical in a row.
          const divider =
            i === 0
              ? ''
              : expand
                ? 'border-t border-border/70 sm:border-t-0 sm:border-l'
                : 'border-t border-border/70';
          return (
            <div
              key={stat.key}
              className={`flex flex-1 items-center gap-3 rounded-xl px-3.5 py-3 ${divider}`}
            >
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                style={{ backgroundColor: PALETTE_MINT }}
              >
                <Icon className="h-5 w-5" style={{ color: PALETTE_FOREST }} aria-hidden />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {stat.label}
                </p>
                <p
                  className="text-lg font-bold leading-tight whitespace-nowrap tabular-nums"
                  style={{ color: PALETTE_FOREST }}
                >
                  {stat.value}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
